// #612 adversarial review, F1 — the URL-backed search/filter state that
// #612 added carries real PII/client data into Sentry unless it's stripped
// first: /participants' `?q=` holds whatever the search box was told to
// search (the page's own copy tells staff to search by email), and
// /cohorts' `?client=`/`?pl=` and /staff's `?name=` hold client and staff
// names. sentry.ts's own header comment says "no email/PII" — @sentry/react
// reports `request.url` on every event and stamps navigation breadcrumbs
// with the URLs a session visited, and Replay (see sentry.ts) records
// alongside those same breadcrumbs, so all three routes needed scrubbing,
// not just the obvious one.
//
// Pure string in, string out — no Sentry types imported here — so this can
// be unit-tested directly (tests/opsSentryScrub.test.ts) without a DOM or an
// SDK instance, mirroring list-url-params.ts's split.

/**
 * Query-string params whose VALUE is scrubbed before a URL leaves this app
 * for Sentry. Keyed to #612's field defs: `q` is the SectionedSearch/search
 * box text on every list route (cohorts/clients/staff/participants), `name`
 * is staff.tsx's name filter, `client`/`pl` are cohorts.tsx's client/PL name
 * filters. Not `status`/`program`/`role`/`delivery`/`from`/`to` — those hold
 * closed-enum tokens or dates, never a person or client's name.
 */
export const SENTRY_SCRUBBED_PARAMS: ReadonlyArray<string> = [
  "q",
  "name",
  "client",
  "pl",
];

const REDACTED = "[Filtered]";

// A real scheme prefix ("https://", "mailto:") is the ONLY shape `new URL(x)`
// can parse with no base — that's what makes a URL absolute. A
// protocol-relative "//host/path" has no scheme, so `new URL("//host/path")`
// throws just like a bare path does; it needs the same dummy base as a
// relative path to parse at all. Everything without a real scheme (a bare
// path Sentry's own breadcrumbs report, e.g. "/cohorts?q=lisa@example.com",
// or a protocol-relative "//host/path") goes through the dummy-base branch,
// and the result is re-flattened back to the shape it came in — path+search
// +hash for a relative input, "//"+host+path+search+hash for a
// protocol-relative one — so a caller comparing breadcrumb.data.to against
// React Router's own (path-only) locations, or a protocol-relative link,
// would otherwise silently stop matching.
function hasScheme(url: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(url);
}

/**
 * Rewrites the value of every `SENTRY_SCRUBBED_PARAMS` key present in `url`
 * to `[Filtered]`. Returns the input unchanged (same reference) if nothing
 * matched or the input isn't a parseable URL — Sentry's own breadcrumb data
 * is not always a full URL (a route name, a boolean-as-string, etc.), and
 * guessing at those is worse than leaving them alone.
 */
export function scrubSentryUrl(url: string): string {
  if (!url) return url;
  const absolute = hasScheme(url);
  const protocolRelative = !absolute && url.startsWith("//");
  let parsed: URL;
  try {
    parsed = new URL(url, absolute ? undefined : "http://sentry-scrub.invalid");
  } catch {
    return url;
  }
  let changed = false;
  for (const key of SENTRY_SCRUBBED_PARAMS) {
    if (parsed.searchParams.has(key)) {
      parsed.searchParams.set(key, REDACTED);
      changed = true;
    }
  }
  if (!changed) return url;
  if (absolute) return parsed.toString();
  if (protocolRelative) {
    return `//${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
  }
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
